import React from 'react'
import { Link } from 'react-router-dom'
import { FileText, ArrowLeft, CheckCircle, ArrowRight } from 'lucide-react'
import { PublicFooter } from '../../components/layout/PublicFooter'
import { PublicNavigation } from '../../components/layout/PublicNavigation'
import { Button } from '../../components/ui/Button'

export const GettingStartedPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-white dark:bg-slate-900 relative overflow-hidden transition-colors duration-300">
      <PublicNavigation />

      {/* Hero Section */}
      <section className="py-20 lg:py-32 relative z-10">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <Link to="/documentation" className="inline-flex items-center text-[#69adba] hover:text-[#5a9aa6] dark:hover:text-[#7bb8c5] mb-6">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Documentation
            </Link>
            <div className="inline-flex items-center justify-center w-16 h-16 bg-[#69adba]/10 dark:bg-[#69adba]/20 rounded-full mb-6">
              <FileText className="h-8 w-8 text-[#69adba]" />
            </div>
            <h1 className="text-4xl font-bold tracking-tight text-slate-900 dark:text-slate-100 sm:text-6xl mb-6">
              Getting Started
            </h1>
            <p className="mt-6 text-lg leading-8 text-slate-600 dark:text-slate-400">
              Learn the basics of setting up your account and starting your first 1031 exchange on Open1031.
            </p>
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-20 relative z-10">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto prose prose-lg dark:prose-invert">
            <div className="space-y-12">
              {/* Step 1 */}
              <div className="bg-white dark:bg-slate-800 p-8 rounded-2xl shadow-lg border border-slate-200 dark:border-slate-700">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#69adba]/10 dark:bg-[#69adba]/20 flex items-center justify-center">
                    <span className="text-[#69adba] font-bold text-lg">1</span>
                  </div>
                  <div className="flex-1">
                    <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-100 mb-3">
                      Create Your Account
                    </h2>
                    <p className="text-slate-600 dark:text-slate-400 mb-4">
                      Start by creating your Open1031 account. You can do this through our AI assistant Neela, 
                      who will guide you through the registration process step by step.
                    </p>
                    <ul className="space-y-2 text-slate-600 dark:text-slate-400">
                      <li className="flex items-start">
                        <CheckCircle className="h-5 w-5 text-[#69adba] mr-2 mt-0.5 flex-shrink-0" />
                        <span>Click "Get Started" on the homepage to open the chat interface</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle className="h-5 w-5 text-[#69adba] mr-2 mt-0.5 flex-shrink-0" />
                        <span>Follow Neela's prompts to provide your basic information</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle className="h-5 w-5 text-[#69adba] mr-2 mt-0.5 flex-shrink-0" />
                        <span>Complete identity verification when prompted</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Step 2 */}
              <div className="bg-white dark:bg-slate-800 p-8 rounded-2xl shadow-lg border border-slate-200 dark:border-slate-700">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#69adba]/10 dark:bg-[#69adba]/20 flex items-center justify-center">
                    <span className="text-[#69adba] font-bold text-lg">2</span>
                  </div>
                  <div className="flex-1">
                    <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-100 mb-3">
                      Start Your First Exchange
                    </h2>
                    <p className="text-slate-600 dark:text-slate-400 mb-4">
                      Once your account is set up, you can begin your first 1031 exchange. Navigate to the 
                      Exchange Hub from your dashboard or use Neela to guide you through the process.
                    </p>
                    <ul className="space-y-2 text-slate-600 dark:text-slate-400">
                      <li className="flex items-start">
                        <CheckCircle className="h-5 w-5 text-[#69adba] mr-2 mt-0.5 flex-shrink-0" />
                        <span>Access the Exchange Hub from the main navigation</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle className="h-5 w-5 text-[#69adba] mr-2 mt-0.5 flex-shrink-0" />
                        <span>Choose to start with Neela (AI assistant) or manually in the Hub</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle className="h-5 w-5 text-[#69adba] mr-2 mt-0.5 flex-shrink-0" />
                        <span>Follow the wizard to enter your exchange details</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Step 3 */}
              <div className="bg-white dark:bg-slate-800 p-8 rounded-2xl shadow-lg border border-slate-200 dark:border-slate-700">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#69adba]/10 dark:bg-[#69adba]/20 flex items-center justify-center">
                    <span className="text-[#69adba] font-bold text-lg">3</span>
                  </div>
                  <div className="flex-1">
                    <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-100 mb-3">
                      Complete Required Information
                    </h2>
                    <p className="text-slate-600 dark:text-slate-400 mb-4">
                      The Exchange Hub will guide you through collecting all necessary information for your 
                      1031 exchange, organized into clear phases.
                    </p>
                    <ul className="space-y-2 text-slate-600 dark:text-slate-400">
                      <li className="flex items-start">
                        <CheckCircle className="h-5 w-5 text-[#69adba] mr-2 mt-0.5 flex-shrink-0" />
                        <span>Phase One: Relinquished Property Sale information</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle className="h-5 w-5 text-[#69adba] mr-2 mt-0.5 flex-shrink-0" />
                        <span>Phase Two: Replacement Property Purchase information</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle className="h-5 w-5 text-[#69adba] mr-2 mt-0.5 flex-shrink-0" />
                        <span>Upload required documents as you progress</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Step 4 */}
              <div className="bg-white dark:bg-slate-800 p-8 rounded-2xl shadow-lg border border-slate-200 dark:border-slate-700">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#69adba]/10 dark:bg-[#69adba]/20 flex items-center justify-center">
                    <span className="text-[#69adba] font-bold text-lg">4</span>
                  </div>
                  <div className="flex-1">
                    <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-100 mb-3">
                      Track Your Progress
                    </h2>
                    <p className="text-slate-600 dark:text-slate-400 mb-4">
                      Monitor your exchange progress through the dashboard. You'll see deadlines, 
                      required actions, and completion status for each phase.
                    </p>
                    <ul className="space-y-2 text-slate-600 dark:text-slate-400">
                      <li className="flex items-start">
                        <CheckCircle className="h-5 w-5 text-[#69adba] mr-2 mt-0.5 flex-shrink-0" />
                        <span>View upcoming deadlines and important dates</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle className="h-5 w-5 text-[#69adba] mr-2 mt-0.5 flex-shrink-0" />
                        <span>Check document verification status</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle className="h-5 w-5 text-[#69adba] mr-2 mt-0.5 flex-shrink-0" />
                        <span>Receive notifications for required actions</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#69adba] dark:bg-[#5a9aa6]">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold tracking-tight text-white sm:text-4xl mb-4">
            Ready to Get Started?
          </h2>
          <p className="mt-4 text-lg text-white/90 mb-8">
            Create your account and start your first 1031 exchange today.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              className="bg-white text-[#69adba] hover:bg-slate-50"
              onClick={() => {
                window.dispatchEvent(new CustomEvent('openChat'))
              }}
            >
              Get Started
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
            <Link to="/documentation">
              <Button variant="outline" className="border-white text-white hover:bg-white/10">
                View All Documentation
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <PublicFooter />
    </div>
  )
}

